import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, FormHelperText } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Select from 'react-select';
import InputGroupV2 from '../InputGroupV2';
import { getReactSelectStyles } from '../reactSelectTheme';
import { useReactSelectTabBlur } from '../reactSelectTabSelect';
import { getSelectNoOptionsMessage, matchSearchSelectOption } from '../selectSearchFilter';

const formatOptionLabel = (option, { context }) => {
  if (!option.chip) return option.label;
  if (context === 'value') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Box
          component="span"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {option.label}
        </Box>
        <Chip
          label={option.chip}
          size="small"
          variant="outlined"
          sx={{ height: 20, fontSize: 11 }}
        />
      </Box>
    );
  }
  return (
    <Box sx={{ py: 0.25 }}>
      <Box sx={{ lineHeight: 1.3 }}>{option.label}</Box>
      <Chip
        label={option.chip}
        size="small"
        variant="outlined"
        sx={{ mt: 0.5, height: 20, fontSize: 11 }}
      />
    </Box>
  );
};

const SearchSelectV2 = ({
  onSelect,
  value,
  isRequired,
  error,
  options,
  label,
  disabled,
  multiSelect,
  sublabelNumericOnly,
}) => {
  const theme = useTheme();
  const { selectRef, tabSelectsValue, onKeyDown } = useReactSelectTabBlur();
  const selectStyles = useMemo(
    () => getReactSelectStyles(theme, { menuZIndex: 99999, error }),
    [theme, error]
  );

  const onChangeLocal = (data) => {
    if (multiSelect) {
      onSelect(data ? data.map((u) => u.value) : null);
    } else {
      onSelect(data.value);
    }
  };

  const DropdownIndicator = () => (
    <ArrowDropDownIcon sx={{ color: 'text.secondary' }} />
  );
  const IndicatorSeparator = () => '';

  const getDefaultValue = () => {
    if (multiSelect && value && options) {
      return options.filter((i) => value.includes(i.value));
    }

    if (value && options) {
      const selectedOptions = options.filter((i) => i.value === value);
      if (selectedOptions && selectedOptions.length > 0) {
        return selectedOptions[0];
      }
    }

    return null;
  };

  return (
    <InputGroupV2 label={label} isRequired={isRequired}>
      <Select
        ref={selectRef}
        components={{ DropdownIndicator, IndicatorSeparator }}
        value={getDefaultValue()}
        styles={selectStyles}
        options={options}
        formatOptionLabel={formatOptionLabel}
        onChange={onChangeLocal}
        onKeyDown={onKeyDown}
        tabSelectsValue={tabSelectsValue}
        placeholder="Selecione..."
        noOptionsMessage={({ inputValue }) =>
          getSelectNoOptionsMessage(inputValue, options?.length ?? 0)
        }
        isMulti={multiSelect}
        isDisabled={disabled}
        filterOption={(candidate, input) =>
          candidate.data.__isNew__ ||
          matchSearchSelectOption({
            label: candidate.label,
            sublabel: candidate.data?.sublabel || candidate.data?.chip,
            input,
            sublabelNumericOnly,
            optionsCount: options?.length ?? 0,
          })
        }
        menuPortalTarget={document.body}
      />
      {error && (
        <FormHelperText error sx={{ mx: 1.5, mt: 1 }}>
          O campo é obrigatório
        </FormHelperText>
      )}
    </InputGroupV2>
  );
};

SearchSelectV2.propTypes = {
  error: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ]),
  options: PropTypes.array.isRequired,
  isRequired: PropTypes.any,
  onSelect: PropTypes.func.isRequired,
  grupos: PropTypes.object,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  ativo: PropTypes.bool,
  multiSelect: PropTypes.bool,
  sublabelNumericOnly: PropTypes.bool,
};

SearchSelectV2.defaultProps = {
  error: false,
  isRequired: false,
  grupos: null,
  disabled: false,
  ativo: null,
  multiSelect: false,
  sublabelNumericOnly: true,
};

export default SearchSelectV2;
